import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const UserReviews = ({ reviews, neighborhoodRating }) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    category: 'property'
  });
  const [filterCategory, setFilterCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Reviews' },
    { id: 'property', label: 'Property' },
    { id: 'neighborhood', label: 'Neighborhood' },
    { id: 'owner', label: 'Owner' },
    { id: 'amenities', label: 'Amenities' }
  ];

  const getFilteredReviews = () => {
    if (filterCategory === 'all') return reviews;
    return reviews.filter(review => review.category === filterCategory);
  };

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    console.log('Review submitted:', newReview);
    alert('Thank you for your review!');
    setNewReview({
      rating: 5,
      title: '',
      comment: '',
      category: 'property'
    });
    setShowReviewForm(false);
  };

  const renderStars = (rating, size = 16) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name={index < rating ? 'Star' : 'Star'}
        size={size}
        className={index < rating ? 'text-warning fill-current' : 'text-border'}
      />
    ));
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const reviewDate = new Date(date);
    const diffTime = Math.abs(now - reviewDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      property: 'Home',
      neighborhood: 'MapPin',
      owner: 'User',
      amenities: 'Star'
    };
    return iconMap[category] || 'MessageCircle';
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="bg-surface rounded-lg shadow-property-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            Reviews & Ratings
          </h2>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowReviewForm(true)}
          >
            Write Review
          </Button>
        </div>

        {/* Overall Rating */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary mb-2">{averageRating}</div>
            <div className="flex justify-center mb-2">
              {renderStars(Math.round(averageRating), 20)}
            </div>
            <div className="text-sm text-text-secondary">
              Based on {reviews.length} reviews
            </div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-secondary mb-2">
              {neighborhoodRating.overall}
            </div>
            <div className="flex justify-center mb-2">
              {renderStars(neighborhoodRating.overall, 20)}
            </div>
            <div className="text-sm text-text-secondary">
              Neighborhood Rating
            </div>
          </div>

          <div className="space-y-2">
            {Object.entries(neighborhoodRating.breakdown).map(([aspect, rating]) => (
              <div key={aspect} className="flex items-center justify-between text-sm">
                <span className="text-text-secondary capitalize">{aspect}</span>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {renderStars(rating, 14)}
                  </div>
                  <span className="text-text-primary font-medium">{rating}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setFilterCategory(category.id)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                filterCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary hover:text-text-primary'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      <div className="p-6">
        <div className="space-y-6">
          {getFilteredReviews().map((review, index) => (
            <div key={index} className="border-b border-border/50 pb-6 last:border-b-0 last:pb-0">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  {review.avatar ? (
                    <img
                      src={review.avatar}
                      alt={review.author}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={20} className="text-primary" />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-medium text-text-primary">{review.author}</div>
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <span>{getTimeAgo(review.date)}</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Icon name={getCategoryIcon(review.category)} size={12} />
                          <span className="capitalize">{review.category}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {renderStars(review.rating)}
                      <span className="text-sm text-text-secondary ml-1">
                        {review.rating}/5
                      </span>
                    </div>
                  </div>

                  {review.title && (
                    <h4 className="font-medium text-text-primary mb-2">
                      {review.title}
                    </h4>
                  )}

                  <p className="text-text-secondary text-sm leading-relaxed mb-3">
                    {review.comment}
                  </p>

                  {review.helpful && (
                    <div className="flex items-center gap-4 text-sm">
                      <button className="flex items-center gap-1 text-text-secondary hover:text-text-primary transition-smooth">
                        <Icon name="ThumbsUp" size={14} />
                        <span>Helpful ({review.helpful})</span>
                      </button>
                      <button className="flex items-center gap-1 text-text-secondary hover:text-text-primary transition-smooth">
                        <Icon name="MessageCircle" size={14} />
                        <span>Reply</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {getFilteredReviews().length === 0 && (
          <div className="text-center py-8">
            <Icon name="MessageCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <div className="text-text-secondary">
              No reviews found for this category.
            </div>
          </div>
        )}
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text-primary">
                  Write a Review
                </h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleReviewSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Rating
                </label>
                <div className="flex gap-1">
                  {Array.from({ length: 5 }, (_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setNewReview({...newReview, rating: index + 1})}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Icon
                        name="Star"
                        size={24}
                        className={index < newReview.rating ? 'text-warning fill-current' : 'text-border'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category
                </label>
                <select
                  value={newReview.category}
                  onChange={(e) => setNewReview({...newReview, category: e.target.value})}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="property">Property</option>
                  <option value="neighborhood">Neighborhood</option>
                  <option value="owner">Owner</option>
                  <option value="amenities">Amenities</option>
                </select>
              </div>

              <Input
                label="Review Title"
                value={newReview.title}
                onChange={(e) => setNewReview({...newReview, title: e.target.value})}
                placeholder="Summarize your experience"
                required
              />

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Your Review
                </label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Share your detailed experience..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReviewForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1"
                >
                  Submit Review
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReviews;